import React, { useState } from 'react'
import { Col, Form, Row } from 'react-bootstrap'
import { addDays, format } from 'date-fns'
import { useDispatch } from 'react-redux'

import useTranslation from '@/Hooks/UseTranslation'
import { useProgrammation } from '@/Hooks/UseProgrammation'
import { useProject } from '@/Hooks/UseProject'
import { useDatePicker } from '@/Hooks/UseDatePicker'
import { getDefaultTaskDates, parseLocalDate, safeFormat } from '@/Helpers/DateFunctions'

import { updateTaskAction } from '@/Redux/Actions/ProgrammationManagerActions'
import { updateTaskBaseDates, updateTaskScheduledDates } from '@/Services/ProgrammationManagerService'

import InputText from '../../components/InputText'

const TaskBaseDates = () => {

  const { t } = useTranslation()

  const dispatch = useDispatch()

  const { itemOpened: task, patchItemOpened, setEditingItem, mode } = useProgrammation()
  const { project, nonWorkingDays } = useProject()

  const { showDateRangePicker, hideDatePicker } = useDatePicker()

  const [ baseDaysReset, setBaseDaysReset ] = useState(0)

  const baseStart = safeFormat(task.baseStart || task.startsAt, 'yyyy-MM-dd')
  const baseEnd = safeFormat(task.baseEnd || task.endsAt, 'yyyy-MM-dd')

  const saveChanges = async (baseStart: string, baseEnd: string) => {
    setEditingItem(true)

    const [ error, data ] = await updateTaskBaseDates(task._id, baseStart, baseEnd)

    setEditingItem(false)

    if (error) {
      return false
    }

    const newItemOpened = {
      ...task,
      baseStart,
      baseEnd,
      baseDays: data.task.baseDays,
    }

    patchItemOpened(newItemOpened)

    await dispatch(updateTaskAction(data.task, data.updatedTasks))

    if (mode === 'create') {
      const [ schedError, schedData ] = await updateTaskScheduledDates(task._id, baseStart, baseEnd)

      if (!schedError && schedData?.task) {
        patchItemOpened({
          ...newItemOpened,
          startsAt: baseStart,
          endsAt: baseEnd,
          executeDays: schedData.task.executeDays,
        })

        await dispatch(updateTaskAction(schedData.task, schedData.updatedTasks))
      }
    }
  }

  const handleChangeDates = async (dates: any) => {

    const {
      startDate,
      endDate
    } = dates

    await saveChanges(startDate, endDate)

    hideDatePicker()
  }

  const handleShowDateRangePicker = () => {

    if (task.hasSubTasks) {
      return
    }

    const defaultDates = getDefaultTaskDates(nonWorkingDays)

    showDateRangePicker({
      startDate: baseStart || defaultDates.startDate,
      endDate: baseEnd || defaultDates.endDate,
      projectStartDate: project.startsAt,
      projectEndDate: project.endsAt,
      nonWorkingDays,
      onChangeRange: handleChangeDates
    })
  }

  const handleChangeBaseDays = async (value: string) => {

    if (task.hasSubTasks) {
      return
    }

    const newBaseDays = parseInt(value, 10)

    if (isNaN(newBaseDays) || newBaseDays < 1) {
      setBaseDaysReset(t => t + 1)
      return
    }

    const parsedBaseStart = parseLocalDate(baseStart)

    if (!parsedBaseStart) {
      setBaseDaysReset(t => t + 1)
      return
    }

    const newBaseEnd = format(addDays(parsedBaseStart, newBaseDays - 1), 'yyyy-MM-dd')

    await saveChanges(baseStart, newBaseEnd)
  }

  return (
    <>
      <Row>
        <Col>
          <h5 className='group-title'>
            {t('PROGRAMMATION_MANAGER:BaseDatesTitleLabel')}
          </h5>
        </Col>
      </Row>
      <Row>

        <Col sm={4}>
          <Form.Group>
            <Form.Label>
              {t('PROGRAMMATION_MANAGER:BaseStartLabel')}
            </Form.Label>
            <InputText
              value={safeFormat(baseStart, 'dd/MM/yyyy')}
              placeholder="dd/mm/yyyy"
              onClick={handleShowDateRangePicker}
              asButton
            />
          </Form.Group>
        </Col>
        <Col sm={4}>
          <Form.Group>
            <Form.Label>
              {t('PROGRAMMATION_MANAGER:BaseEndLabel')}
            </Form.Label>
            <InputText
              value={safeFormat(baseEnd, 'dd/MM/yyyy')}
              placeholder="dd/mm/yyyy"
              onClick={handleShowDateRangePicker}
              asButton
            />
          </Form.Group>
        </Col>
        <Col sm={4}>
          <Form.Group>
            <Form.Label>
              {t('PROGRAMMATION_MANAGER:BaseDaysLabel')}
            </Form.Label>
            <InputText
              key={`baseDays-${baseDaysReset}`}
              value={task.baseDays || task.executeDays}
              onChange={handleChangeBaseDays}
              style={{
                padding: '0.375rem 0.75rem !important'
              }}
            />
          </Form.Group>
        </Col>
      </Row>
    </>
  )
}

export default TaskBaseDates